import { useMemo, useState } from 'react'
import { buildSkeleton, type HumanoidModel, type SimState } from '@/domain/hs1'
import { mjcfModel, rigFromModel, toUrdf } from '@/domain/rig'
import { useAuth } from '@/auth/useAuth'
import { Alert, Badge, Button, Card, Input, Select, Table, Td, Textarea, Th } from '@/components/ui'
import { supabase, useAction, useQuery } from '@/lib/data'
import { useSimulation } from '@/sim/SimulationProvider'
import { Metric, PageHead, num } from '@/sim/parts'

/**
 * Modellstände — speichern, festschreiben, vergleichen.
 *
 * WOFÜR. Der Simulator rechnet alles aus einem Zahlenstand, und der lebte nur
 * in der Sitzung. Wer einen Punkt verschob, konnte den vorigen Stand nicht
 * wiederherstellen und zwei Auslegungen nicht nebeneinanderlegen. Für ein
 * Werkzeug, dessen Zweck der Vergleich von Auslegungen ist, ist das die Lücke.
 *
 * FESTGESCHRIEBEN HEISST UNVERÄNDERLICH, und zwar in der Datenbank. Ein Stand,
 * auf den sich jemand beruft, darf sich nicht mehr ändern — sonst ist die
 * Berufung wertlos. Das erzwingt ein Trigger und nicht diese Seite; hier steht
 * nur der Knopf.
 *
 * DER VERGLEICH ZÄHLT NUR DIE UNTERSCHIEDE. Zwei Stände nebeneinander mit
 * dreihundert gleichen Zeilen sind keine Auskunft. Gezeigt wird, was sich
 * unterscheidet — und wenn nichts, steht das da.
 */

interface Version {
  id: string
  space_id: string
  name: string
  note: string
  robot: string
  model: HumanoidModel
  state: SimState
  metrics: Record<string, number>
  frozen_at: string | null
  created_at: string
}

export function VersionsPage() {
  const { model, state, simulation, robot, setEdit } = useSimulation()
  const { spaces } = useAuth()

  const [space, setSpace] = useState('')
  const [name, setName] = useState('')
  const [note, setNote] = useState('')
  const [left, setLeft] = useState('')
  const [right, setRight] = useState('')

  const at = space || spaces[0]?.id || ''
  const save = useAction()
  const freeze = useAction()

  const list = useQuery<Version[]>(`versions:${at}`, () =>
    supabase
      .from('model_versions')
      .select('*')
      .eq('space_id', at)
      .order('created_at', { ascending: false }),
  )

  const versions = list.data ?? []
  const a = versions.find((v) => v.id === left)
  const b = versions.find((v) => v.id === right)

  /*
    Der Export hängt am AKTUELLEN Stand und nicht an einem gespeicherten: was
    hier heruntergeladen wird, ist das, was die Seiten daneben zeigen. Wer den
    Export eines gespeicherten Standes braucht, lädt ihn erst.
  */
  const urdf = useMemo(() => {
    const skeleton = buildSkeleton(model)
    return toUrdf(model, skeleton, rigFromModel(model, state.off), {
      name: robot.id,
      off: new Set(Object.keys(state.off)),
    })
  }, [model, state.off, robot.id])

  /*
    Dasselbe Modell für MuJoCo. Kein zweiter Export desselben: MJCF trägt
    Momentgrenzen und Getriebe, die URDF nicht ausdrücken kann.
  */
  const mjcf = useMemo(() => {
    const skeleton = buildSkeleton(model)
    return mjcfModel(model, skeleton, rigFromModel(model, state.off), {
      name: robot.id,
      off: new Set(Object.keys(state.off)),
    })
  }, [model, state.off, robot.id])

  const store = async () => {
    if (!at || !name.trim()) return
    await save.run(() =>
      supabase.from('model_versions').insert({
        space_id: at,
        name: name.trim(),
        note: note.trim(),
        robot: robot.id,
        model,
        state,
        /* Eine Momentaufnahme für die Liste, ausdrücklich keine Wahrheit — wer
           die Zahl genau braucht, lädt den Stand und rechnet ihn. */
        metrics: {
          speed: simulation.metrics.speed,
          mass: simulation.metrics.mass,
          netPower: simulation.metrics.netPower,
          worstMomentUse: simulation.metrics.worstMomentUse,
        },
      }),
    )
    setName('')
    setNote('')
    list.reload()
  }

  return (
    <div className="stack">
      <PageHead
        title="Modellstände"
      />

      <div className="metrics">
        <Metric label="Stände im Bereich" value={String(versions.length)} note={list.loading ? 'lädt …' : 'gespeichert'} />
        <Metric
          label="Festgeschrieben"
          value={String(versions.filter((v) => v.frozen_at).length)}
          note="unveränderlich"
        />
        <Metric label="Aktuell" value={robot.name} note={`Gangart ${state.gait}`} />
        <Metric
          label="URDF"
          value={`${(urdf.length / 1024).toFixed(0)} kB`}
          note={`${(urdf.match(/<joint /g) ?? []).length} Gelenke`}
        />
        <Metric
          label="MJCF"
          value={`${num(mjcf.mass, 2)} kg`}
          note={`${mjcf.bodies.length} Körper · ${mjcf.joints.length} Gelenke`}
        />
      </div>

      {spaces.length === 0 && (
        <Alert tone="info" title="Kein Bereich">
          Modellstände hängen an einem Bereich. Ohne Mitgliedschaft gibt es keinen Ort, an dem ein
          Stand liegen könnte.
        </Alert>
      )}

      <Card
        title="Diesen Stand speichern"
        actions={
          spaces.length > 1 && (
            <Select value={at} onChange={(e) => setSpace(e.currentTarget.value)} aria-label="Bereich">
              {spaces.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </Select>
          )
        }
      >
        <div className="version__form">
          <label className="version__field">
            <span>Name</span>
            <Input
              value={name}
              onChange={(e) => setName(e.currentTarget.value)}
              placeholder="z. B. Knie 20 mm höher"
            />
          </label>
          <label className="version__field version__field--wide">
            <span>Notiz</span>
            <Textarea
              value={note}
              rows={2}
              onChange={(e) => setNote(e.currentTarget.value)}
              placeholder="Was ist anders und warum"
            />
          </label>
          <Button variant="primary" onClick={() => void store()} disabled={!at || !name.trim() || save.busy}>
            {save.busy ? 'speichert …' : 'Speichern'}
          </Button>
        </div>
        {save.error && (
          <Alert tone="danger" title="Nicht gespeichert">
            {save.error}
          </Alert>
        )}
        <p className="metric__note">
          Gespeichert wird der ganze Zahlenstand, nicht ein Verweis. Ein Stand bleibt damit lesbar,
          auch wenn der Grundstand des Roboters sich später ändert.
        </p>
      </Card>

      <Card flush title="Stände" eyebrow={list.error ?? undefined}>
        {versions.length === 0 ? (
          <p className="page-lead" style={{ padding: 'var(--space-4)' }}>
            {list.loading ? 'Lädt …' : 'Noch kein Stand gespeichert.'}
          </p>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Name</Th>
                <Th>Notiz</Th>
                <Th align="right">Geschw.</Th>
                <Th align="right">Masse</Th>
                <Th align="right">Leistung</Th>
                <Th>Stand</Th>
                <Th align="right">Aktion</Th>
              </tr>
            </thead>
            <tbody>
              {versions.map((v) => (
                <tr key={v.id}>
                  <Td>{v.name}</Td>
                  <Td>{v.note || <span className="cell-muted">—</span>}</Td>
                  <Td align="right" numeric>
                    {num(v.metrics.speed ?? NaN, 2)} m/s
                  </Td>
                  <Td align="right" numeric>
                    {num(v.metrics.mass ?? NaN, 1)} kg
                  </Td>
                  <Td align="right" numeric>
                    {num(v.metrics.netPower ?? NaN, 0)} W
                  </Td>
                  <Td>
                    {v.frozen_at ? (
                      <Badge tone="success">festgeschrieben</Badge>
                    ) : (
                      <Badge tone="neutral">Entwurf</Badge>
                    )}
                  </Td>
                  <Td align="right">
                    <div className="version__actions">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          /* Laden heisst: die Punkte des Standes als Änderungen
                             setzen. Der Grundstand des Roboters bleibt, was er
                             ist — sonst wäre nicht mehr erkennbar, was von wem
                             stammt. */
                          for (const [key, point] of Object.entries(v.model.points)) {
                            for (const axis of ['x', 'y', 'z'] as const) {
                              const now = model.points[key as keyof typeof model.points]?.[axis]
                              if (now !== undefined && point[axis] !== now) {
                                setEdit(`point.${key}.${axis}`, point[axis])
                              }
                            }
                          }
                        }}
                      >
                        Laden
                      </Button>
                      {!v.frozen_at && (
                        <Button
                          size="sm"
                          variant="secondary"
                          disabled={freeze.busy}
                          onClick={() =>
                            void freeze
                              .run(() => supabase.rpc('freeze_model_version', { p_version: v.id }))
                              .then(() => list.reload())
                          }
                        >
                          Festschreiben
                        </Button>
                      )}
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      {freeze.error && (
        <Alert tone="danger" title="Nicht festgeschrieben">
          {freeze.error}
        </Alert>
      )}

      {versions.length > 1 && (
        <Card
          title="Vergleich"
          actions={
            <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
              <Select value={left} onChange={(e) => setLeft(e.currentTarget.value)} aria-label="Stand links">
                <option value="">links wählen</option>
                {versions.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.name}
                  </option>
                ))}
              </Select>
              <Select value={right} onChange={(e) => setRight(e.currentTarget.value)} aria-label="Stand rechts">
                <option value="">rechts wählen</option>
                {versions.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.name}
                  </option>
                ))}
              </Select>
            </div>
          }
        >
          {a && b ? <Diff a={a} b={b} /> : <p className="page-lead">Zwei Stände wählen.</p>}
        </Card>
      )}

      <Card
        title="URDF"
        actions={
          <Button size="sm" variant="secondary" onClick={() => download(`${robot.id}.urdf`, urdf)}>
            Herunterladen
          </Button>
        }
      >
        <p className="page-lead">
          Der Achsbaum in dem Format, das Gazebo, MuJoCo und ROS lesen. Erzeugt aus dem aktuellen
          Stand, nicht gepflegt: wer einen Punkt verschiebt, bekommt eine andere Datei.
        </p>
        <p className="metric__note">
          Geschlossene Ketten — Knie und Rumpf — stehen als ein Drehgelenk darin; URDF kennt keine
          Schleifen. Die Übersetzung steht als Kommentar an der Achse. Trägheiten sind aus Zylindern
          gerechnet und nicht gemessen; das steht auch in der Datei.
        </p>
        <pre className="version__urdf">{urdf.split('\n').slice(0, 18).join('\n')}
…</pre>
      </Card>

      <Card
        title="MJCF für MuJoCo"
        actions={
          <Button size="sm" variant="secondary" onClick={() => download(`${robot.id}.xml`, mjcf.xml)}>
            Herunterladen
          </Button>
        }
      >
        <p className="page-lead">
          Dasselbe Modell in dem Format, das die zwei Dinge trägt, die URDF nicht ausdrücken kann:
          Momentgrenze je Achse und Übersetzung am Antrieb.
          Damit lässt sich vorwärts rechnen, was dieser Kern rückwärts rechnet.
        </p>
        <div className="metrics">
          <Metric label="Masse" value={`${num(mjcf.mass, 2)} kg`} note="Summe der Körper" />
          <Metric label="Körper" value={String(mjcf.bodies.length)} note="mit Masse" />
          <Metric
            label="Antriebe"
            value={String(mjcf.joints.filter((j) => !j.spring).length)}
            note={`${mjcf.joints.filter((j) => j.spring).length} passive Federn`}
          />
        </div>
        {mjcf.gaps.length > 0 && (
          <Alert tone="warning" title="Was das MJCF nicht rechnen kann">
            <ul>
              {mjcf.gaps.map((g) => (
                <li key={g.node}>
                  <b>
                    {g.node} ({g.axes.join(', ')})
                  </b>{' '}
                  — {g.reason}.
                </li>
              ))}
            </ul>
          </Alert>
        )}
        <p className="metric__note">
          Die Massen kommen aus den Segmenten des Rechenkerns und nicht aus der Stückliste — sonst
          wären es zwei Roboter, die man hinterher vergleicht. Kontaktgrössen sind angenommen und
          stehen mit Vorbehalt in der Datei.
        </p>
        <pre className="version__urdf">{mjcf.xml.split('\n').slice(0, 18).join('\n')}
…</pre>
      </Card>
    </div>
  )
}

/* -------------------------------------------------------------------------
   Der Vergleich
   ------------------------------------------------------------------------- */

/**
 * Was sich zwischen zwei Ständen unterscheidet.
 *
 * NUR DIE UNTERSCHIEDE. Zwei Stände nebeneinander mit dreihundert gleichen
 * Zeilen sind keine Auskunft — man sucht dann von Hand nach der einen Zeile,
 * die anders ist, und übersieht sie.
 */
function Diff({ a, b }: { a: { name: string; model: HumanoidModel }; b: { name: string; model: HumanoidModel } }) {
  const rows = useMemo(() => {
    const out: { what: string; left: number; right: number }[] = []
    for (const [key, point] of Object.entries(a.model.points)) {
      const other = b.model.points[key as keyof typeof b.model.points]
      if (!other) continue
      for (const axis of ['x', 'y', 'z'] as const) {
        if (Math.abs(point[axis] - other[axis]) > 1e-9) {
          out.push({ what: `${point.label} · ${axis}`, left: point[axis], right: other[axis] })
        }
      }
    }
    return out
  }, [a, b])

  if (rows.length === 0) {
    return (
      <p className="page-lead">
        Die Punktesätze von „{a.name}" und „{b.name}" sind gleich.
      </p>
    )
  }

  return (
    <Table>
      <thead>
        <tr>
          <Th>Punkt</Th>
          <Th align="right">{a.name}</Th>
          <Th align="right">{b.name}</Th>
          <Th align="right">Unterschied</Th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.what}>
            <Td>{row.what}</Td>
            <Td align="right" numeric>
              {num(row.left, 1)}
            </Td>
            <Td align="right" numeric>
              {num(row.right, 1)}
            </Td>
            <Td align="right" numeric>
              {row.right > row.left ? '+' : ''}
              {num(row.right - row.left, 1)} mm
            </Td>
          </tr>
        ))}
      </tbody>
    </Table>
  )
}

/**
 * Eine Datei anbieten.
 *
 * Über ein Objekt-URL und nicht über einen Server: die Datei entsteht im
 * Browser aus Zahlen, die dort schon liegen. Ein Rundgang über eine
 * Serverfunktion wäre ein zweiter Ort, an dem derselbe Export gerechnet würde.
 */
function download(filename: string, text: string) {
  const url = URL.createObjectURL(new Blob([text], { type: 'application/xml' }))
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  link.click()
  URL.revokeObjectURL(url)
}
